import { useCallback, useEffect, useMemo, useState } from 'react';
import { MANUALLY_ADDED } from '../../../core/constants';
import { getAllNewTitles } from '../columns/getAllNewTitles';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { Delete, ToCRM, Watch, ToPodiumTitle } from '../../../composed-components/Actions';
import { BASEFONTSIZE, PAGESIZE, REFETCHDELAY, RefetchOptions } from '../constants';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import ResizeTable from '../../../composed-components/Resize';
import { EFontSize, TColumns } from '../types/types';
import ToPendingExclusion from '../../../composed-components/Actions/ToPendingExclusion';
import Pagination from '../../../components/Pagination';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import Legend from '../../../composed-components/Legend';
import { TableFooter } from '../../../components/Table';
import { useActions } from '../../../context/Actions.context';
import getDataSource from '../utils/getDataSource';

const ManuallyAdded = () => {
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);
  const [page, setPage] = useState<number>(1);

  const { edit_titles, move_titles_hubspot } = useUserPermit();
  const { isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { filter } = useFilter();
  const { sort } = useSort();

  const changePage = p => setPage(p);
  const deleteBooks = book => setDeleteItems(book);
  const changeFontSize = size => setFontSize(size);

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    MANUALLY_ADDED, {
      page,
      ...filter[MANUALLY_ADDED],
      ...sort[MANUALLY_ADDED]
    });

  const { count, refetch: refetchRowCount } = useDataRowCount(MANUALLY_ADDED, { ...filter[MANUALLY_ADDED] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortComponent = useCallback(
    (title: string) => (
      <Sorting id={MANUALLY_ADDED} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => {
    const columns = getAllNewTitles(sortComponent, MANUALLY_ADDED);

    if (edit_titles) {
      columns.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            <Delete book={book} id={MANUALLY_ADDED} />
            <ToPendingExclusion book={book} id={MANUALLY_ADDED} />
            <ToPodiumTitle book={book} id={MANUALLY_ADDED} />
            <Watch book={book} id={MANUALLY_ADDED} />
            {move_titles_hubspot && <ToCRM book={book} id={MANUALLY_ADDED} />}
          </div>
        )
      });
    }

    return columns;
  }, [edit_titles, move_titles_hubspot, sortComponent]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data);

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }
  }, [page, filter[MANUALLY_ADDED], sort[MANUALLY_ADDED]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm id={MANUALLY_ADDED} page={page} changePage={changePage} />
        <Settings
          thead={thead}
          id={MANUALLY_ADDED}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteBooks={deleteBooks}
          deleteItems={deleteItems}
        />
      </div>

      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            id={MANUALLY_ADDED}
            dataSource={dataSource}
            deleteItems={deleteItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter>
            <Legend map={['crm', 'podium', 'price']} />
            <Pagination
              total={count}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default ManuallyAdded;